import { memo } from 'react';
import { FormGroup, Input, Label, Table } from 'reactstrap';
import { Controller, useFormContext } from 'react-hook-form';
import intl from 'react-intl-universal';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';

import Radio from 'components/Radio';
import Checkbox from 'components/Checkbox';
import Select from 'components/Selectes/SimpleSelect';
import MultiSelect from 'components/Selectes/MultiSelect';
import {
  EQuestionType,
  TQuestion,
  EUnitSystem,
  IHealthInfo,
} from 'modules/forms/interfaces/form.interfaces';
import { calculateHealthBMI, convertFeetAndInchesToInches } from '../helpers/healthInfo.helper';

interface IFormQuestionProps {
  question: TQuestion;
  sectionTitle?: string;
  index?: number;
  isLast?: boolean;
}

const FormQuestion = ({
  question,
  sectionTitle,
  index = 0,
  isLast,
}: IFormQuestionProps): JSX.Element => {
  const { control } = useFormContext();

  if (!question) {
    return <></>;
  }

  const renderTitleTable = () => {
    return question.titleTable ? (
      <Table bordered responsive size="sm" className="mb-sm">
        <thead>
          <tr>
            {question.titleTable.headers.map(header => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {question.titleTable.rows.map(row => (
            <tr key={row.join('-')}>
              {row.map(cell => (
                <td key={uuid()}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    ) : null;
  };

  switch (question.type) {
    case EQuestionType.SHORT_TEXT: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field }) => (
            <div className="form-question form-question--text">
              <div className="form-question-body">
                <FormGroup className="form-floating-label">
                  <div className="mb-sm">{question.title}</div>
                  <Input
                    placeholder={question.placeholder}
                    bsSize="lg"
                    autoComplete="one-time-code"
                    {...field}
                  />
                </FormGroup>
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.LONG_TEXT: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field }) => (
            <div className="form-question form-question--textarea">
              <div className="form-question-body">
                <FormGroup className="form-floating-label">
                  <div className="mb-sm">{question.title}</div>
                  <Input
                    placeholder={question.placeholder}
                    bsSize="lg"
                    type="textarea"
                    autoComplete="one-time-code"
                    {...field}
                  />
                </FormGroup>
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.DATE: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field }) => (
            <div className="form-question form-question--binary">
              <div className="form-question-body">
                <FormGroup className="form-floating-label">
                  <div className="mb-sm">{question.title}</div>
                  <Input placeholder={question.title} bsSize="lg" type="date" {...field} />
                </FormGroup>
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.DROPDOWN: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field }) => (
            <div className="form-question form-question--dropdown">
              <div className="form-question-body">
                <FormGroup
                  className={classNames('form-floating-label', {
                    'form-select--not-searchable': !question.isSearchable,
                  })}
                >
                  <div className="mb-sm">{question.title}</div>
                  <Select<{ name: string }>
                    options={question.options.map(g => ({
                      name: g,
                    }))}
                    optionLabel={option => option.name}
                    getOptionValue={({ name }) => name}
                    isSearchable={!!question.isSearchable}
                    value={field.value ? { name: field.value } : undefined}
                    block
                    size="small"
                    onChange={val => {
                      const { name } = (val || {}) as { name: string };
                      field.onChange(name);
                    }}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isOptionSelected={(option, select) => option.name === select[0]?.name}
                  />
                </FormGroup>
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.MULTI_DROPDOWN: {
      return (
        <Controller
          name={`questions.${index}.values`}
          control={control}
          render={({ field }) => (
            <div className="form-question form-question--binary">
              <div className="form-question-body">
                <FormGroup
                  className={classNames('form-floating-label', {
                    'form-select--not-searchable': !question.isSearchable,
                  })}
                >
                  <div className="mb-sm">{question.title}</div>
                  <MultiSelect<{ name: string }>
                    options={question.options.map(g => ({
                      name: g,
                    }))}
                    optionLabel={option => option.name}
                    getOptionValue={({ name }) => name}
                    value={field.value?.map((type: string) => ({ name: type }))}
                    onChange={val => {
                      const data = val as { name: string }[];
                      field.onChange(data?.length ? data.map(item => item.name) : null);
                    }}
                    isSearchable={!!question.isSearchable}
                    block
                    menuPortalTarget={document.body}
                    isOptionSelected={(option, selected) =>
                      selected.map(val => val.name).includes(option.name)
                    }
                  />
                </FormGroup>
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.CHECKBOXES: {
      return (
        <Controller
          name={`questions.${index}.values`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="form-question form-question--checkboxes">
              {(!!sectionTitle || !!question.title) && (
                <div className="form-question-header">
                  {!!sectionTitle && (
                    <h3 className="form-question__section-title">{sectionTitle}</h3>
                  )}
                  {!!question.title && <div className="form-question__title">{question.title}</div>}
                </div>
              )}
              <div className="form-question-body">
                {question.options.map(item => (
                  <Checkbox
                    id={item}
                    name={item}
                    label={item}
                    checked={value?.includes(item)}
                    onChange={e => {
                      const isChecked = e.target.checked;
                      const noneOption = question.options[question.options.length - 1];

                      if (question.hasNoneOption) {
                        if (item === noneOption) {
                          onChange(isChecked ? [item] : value?.filter((o: string) => o !== item));
                        } else {
                          onChange(
                            isChecked
                              ? [...(value || []), item].filter((o: string) => o !== noneOption)
                              : value?.filter((o: string) => o !== item),
                          );
                        }
                      } else {
                        onChange(
                          isChecked
                            ? [...(value || []), item]
                            : value?.filter((o: string) => o !== item),
                        );
                      }
                    }}
                  />
                ))}
                {!!value?.length &&
                  !!question.showAdditionalInfo &&
                  !(
                    question.hasNoneOption &&
                    value?.includes(question.options[question.options.length - 1])
                  ) && (
                    <Controller
                      name={`questions.${index}.additionalInfo`}
                      control={control}
                      render={({ field }) => (
                        <FormGroup className="mb-0 form-question__additional-info">
                          {!!question.additionalInfoLabel && (
                            <Label className="mb-sm">{question.additionalInfoLabel}</Label>
                          )}
                          <Input
                            bsSize="lg"
                            placeholder={
                              question.additionalInfoPlaceholder ||
                              'Additional Information (optional)'
                            }
                            type="textarea"
                            autoComplete="one-time-code"
                            {...field}
                          />
                        </FormGroup>
                      )}
                    />
                  )}
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.RADIO: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="form-question form-question--radio">
              {!!question.title && (
                <div className="form-question-header">
                  <h3 className="form-question__section-title">{question.title}</h3>
                  {!!question.description && (
                    <div className="form-question--text">{question.description}</div>
                  )}
                  {renderTitleTable()}
                </div>
              )}
              <div className="form-question-body">
                {question.options.map(option => (
                  <Radio
                    checked={value === option}
                    onChange={() => onChange(option)}
                    label={option}
                  />
                ))}
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.ADDRESS: {
      return (
        <div className="form-question form-question--address">
          {!!question.title && (
            <div className="form-question-header">
              {!!question.title && <div className="form-question__title">{question.title}</div>}
            </div>
          )}
          <div className="form-question-body">
            <div className="mb-md">
              <Controller
                name={`questions.${index}.value.streetAddress`}
                control={control}
                render={({ field }) => (
                  <FormGroup className="form-floating-label">
                    <div className="mb-sm">Address Line 1</div>
                    <Input placeholder="Address Line 1" bsSize="lg" {...field} />
                  </FormGroup>
                )}
              />
            </div>

            <div className="mb-md flex items-center gap-md">
              <Controller
                name={`questions.${index}.value.address2`}
                control={control}
                render={({ field }) => (
                  <FormGroup className="form-floating-label flex-1">
                    <div className="mb-sm">Address Line 2</div>
                    <Input placeholder="Address Line 2" bsSize="lg" {...field} />
                  </FormGroup>
                )}
              />
            </div>

            <div className="mb-md flex items-center gap-md">
              <Controller
                name={`questions.${index}.value.city`}
                control={control}
                render={({ field }) => (
                  <FormGroup className="form-floating-label flex-1">
                    <div className="mb-sm">City</div>
                    <Input placeholder="City" bsSize="lg" {...field} />
                  </FormGroup>
                )}
              />
              <Controller
                name={`questions.${index}.value.country`}
                control={control}
                render={({ field }) => (
                  <FormGroup className="form-floating-label flex-1">
                    <div className="mb-sm">Country</div>
                    <Input placeholder="Country" bsSize="lg" {...field} />
                  </FormGroup>
                )}
              />
            </div>

            <Controller
              name={`questions.${index}.value.postalCode`}
              control={control}
              render={({ field }) => (
                <FormGroup className="form-floating-label flex-1">
                  <div className="mb-sm">Postal Code</div>
                  <Input
                    placeholder="Postal Code"
                    bsSize="lg"
                    autoCapitalize="characters"
                    {...field}
                    onChange={e => {
                      field.onChange(e.target.value ? e.target.value.toUpperCase() : '');
                    }}
                  />
                </FormGroup>
              )}
            />
          </div>
        </div>
      );
    }
    case EQuestionType.BINARY: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="form-question form-question--binary">
              <div className="form-question-body">
                <div className="form-question-title">
                  <div className="flex-1">
                    <div className="mb-sm">{question.title}</div>
                    {!!question.description && (
                      <div className="form-question--text">{question.description}</div>
                    )}
                  </div>
                  <FormGroup className="flex items-center gap-m">
                    <Radio
                      classNameWrap="mb-0"
                      checked={value === true}
                      onChange={() => {
                        onChange(true);
                        if (isLast) {
                          setTimeout(() => {
                            const scroll = document.getElementById('page-main-scrollable');
                            if (scroll) {
                              scroll.scrollTop = scroll.scrollHeight;
                            }
                          }, 100);
                        }
                      }}
                      label="Yes"
                    />
                    <Radio
                      classNameWrap="mb-0"
                      checked={value === false}
                      onChange={() => onChange(false)}
                      label="No"
                    />
                  </FormGroup>
                </div>
                {!!value && !!question.showAdditionalInfo && (
                  <Controller
                    name={`questions.${index}.additionalInfo`}
                    control={control}
                    render={({ field }) => (
                      <FormGroup className="mb-0 form-question__additional-info">
                        {!!question.additionalInfoLabel && (
                          <Label className="mb-sm">{question.additionalInfoLabel}</Label>
                        )}
                        <Input
                          bsSize="lg"
                          placeholder={
                            question.additionalInfoPlaceholder ||
                            'Additional Information (optional)'
                          }
                          type="textarea"
                          autoComplete="one-time-code"
                          {...field}
                        />
                      </FormGroup>
                    )}
                  />
                )}
              </div>
            </div>
          )}
        />
      );
    }
    case EQuestionType.HEALTH_MEASUREMENT: {
      return (
        <Controller
          name={`questions.${index}.value`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="form-question form-question--health-measurement">
              <div className="form-question-body">
                <div className="form-question-title">
                  <div className="flex-1">
                    <div className="mb-sm">{question.title}</div>
                    {!!question.description && (
                      <div className="form-question--text">{question.description}</div>
                    )}
                  </div>
                </div>
                <div className="health-measurement-inputs">
                  <FormGroup
                    className="form-floating-label form-select--not-searchable"
                    style={{ marginBottom: '16px' }}
                  >
                    <div className="mb-sm">Measurement System</div>
                    <Select<{ name: EUnitSystem }>
                      options={[{ name: EUnitSystem.IMPERIAL }, { name: EUnitSystem.METRIC }]}
                      optionLabel={option => intl.get(`units.${option.name}`)}
                      getOptionValue={({ name }) => name}
                      value={value?.unitSystem ? { name: value.unitSystem } : undefined}
                      onChange={val => {
                        const newValue = { ...value } as IHealthInfo;
                        const selectedValue = val as { name: EUnitSystem };
                        newValue.unitSystem = selectedValue?.name;
                        onChange(newValue);
                      }}
                      isSearchable={false}
                      block
                      size="small"
                      isOptionSelected={(option, selected) => option.name === selected[0]?.name}
                    />
                  </FormGroup>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                    {value?.unitSystem === EUnitSystem.IMPERIAL ? (
                      <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
                        <FormGroup className="form-floating-label" style={{ flex: 1 }}>
                          <div className="mb-sm">Height (feet)</div>
                          <Input
                            type="number"
                            bsSize="lg"
                            value={value?.feet || ''}
                            placeholder="Enter number"
                            onChange={e => {
                              const newValue = { ...value } as IHealthInfo;
                              newValue.feet = e.target.value;
                              newValue.height = convertFeetAndInchesToInches(
                                e.target.value,
                                newValue.inches,
                              );
                              onChange(newValue);
                            }}
                            step={1}
                          />
                        </FormGroup>
                        <FormGroup className="form-floating-label" style={{ flex: 1 }}>
                          <div className="mb-sm">Height (inches)</div>
                          <Input
                            type="number"
                            bsSize="lg"
                            value={value?.inches || ''}
                            placeholder="Enter number"
                            onChange={e => {
                              const newValue = { ...value } as IHealthInfo;
                              newValue.inches = e.target.value;
                              newValue.height = convertFeetAndInchesToInches(
                                newValue.feet,
                                e.target.value,
                              );
                              onChange(newValue);
                            }}
                            step={1}
                          />
                        </FormGroup>
                      </div>
                    ) : (
                      <FormGroup className="form-floating-label" style={{ flex: 1 }}>
                        <div className="mb-sm">Height (cm)</div>
                        <Input
                          type="number"
                          bsSize="lg"
                          value={value?.height || ''}
                          placeholder="Enter number"
                          onChange={e => {
                            const newValue = { ...value } as IHealthInfo;
                            newValue.height = e.target.value;
                            onChange(newValue);
                          }}
                          step={1}
                        />
                      </FormGroup>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '16px',
                      alignItems: 'flex-end',
                      flex: 1,
                      marginTop: '16px',
                    }}
                  >
                    <FormGroup className="form-floating-label" style={{ flex: 1 }}>
                      <div className="mb-sm">
                        Weight ({value.unitSystem === EUnitSystem.IMPERIAL ? 'stone' : 'kg'})
                      </div>
                      <Input
                        type="number"
                        bsSize="lg"
                        value={value?.weight || ''}
                        placeholder="Enter number"
                        onChange={e => {
                          const newValue = { ...value } as IHealthInfo;
                          newValue.weight = e.target.value;
                          onChange(newValue);
                        }}
                        step={1}
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <FormGroup className="form-floating-label" style={{ marginTop: '16px' }}>
                <div>BMI</div>
                <Input placeholder="BMI" value={calculateHealthBMI(value)} disabled bsSize="lg" />
              </FormGroup>
            </div>
          )}
        />
      );
    }
    case EQuestionType.SEPARATOR: {
      return <div className="form-questions-separator" />;
    }
    default: {
      return <></>;
    }
  }
};

export default memo(FormQuestion);
